import {
  ArrowRightIcon,
  BriefcaseIcon,
  DocumentMagnifyingGlassIcon,
  PuzzlePieceIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { EXTENSION_URLS } from '../../utils/constants'
import Icon from '../Icon'

type Props = {
  applicationsCount: number
  realApplicationsCount: number
  resumeAnalysesCount: number
  productTourCompleted: boolean
  onFocusAddJob: () => void
  onStartProductTour: () => void
}

export const ActivationCards = ({
  applicationsCount,
  realApplicationsCount,
  resumeAnalysesCount,
  productTourCompleted,
  onFocusAddJob,
  onStartProductTour,
}: Props) => {
  const showAddJob = realApplicationsCount === 0
  const showResume = resumeAnalysesCount === 0
  const showTour = !productTourCompleted

  if (!showAddJob && !showResume && !showTour) {
    return null
  }

  return (
    <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {showAddJob && (
        <button
          type="button"
          onClick={onFocusAddJob}
          className="group rounded-xl dark:rounded-2xl border border-app-line bg-app-highlight/70 p-4 text-left transition hover:border-app-accent hover:bg-app-highlight/70 hover:shadow-app-hover shadow-app-card hover:translate-y-0"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-app-primary text-white transition group-hover:scale-105">
              <BriefcaseIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-app-ink font-display">Add your first job</h3>
              <p className="mt-1 text-xs leading-5 text-app-muted">
                {applicationsCount > 0
                  ? 'Your demo jobs are ready. Add a real opportunity when you are ready.'
                  : 'Paste a URL or add a role manually when you are ready.'}
              </p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-app-accent">
                Start from dashboard
                <ArrowRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </div>
          </div>
        </button>
      )}

      {showResume && (
        <div className="rounded-xl dark:rounded-2xl border border-app-line bg-app-highlight/70 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-app-primary text-white">
              <DocumentMagnifyingGlassIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-app-ink font-display">Analyze your resume</h3>
              <p className="mt-1 text-xs leading-5 text-app-muted">
                Use your free analysis for ATS feedback and keyword gaps.
              </p>
              <Link
                to="/resume"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-app-accent hover:text-app-accent-hover"
              >
                Open resume analysis
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl dark:rounded-2xl border border-app-line bg-app-highlight/70 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-app-primary text-white">
            <PuzzlePieceIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-app-ink font-display">Save jobs in one click</h3>
            <p className="mt-1 text-xs leading-5 text-app-muted">
              Install the extension for your main browser.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={EXTENSION_URLS.chrome} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 border border-app-line/80 px-2 py-1 text-xs font-semibold text-app-accent hover:bg-app-highlight rounded-lg">
                <Icon type="chrome" size={14} />
                Chrome
              </a>
              <a href={EXTENSION_URLS.firefox} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 border border-app-line/80 px-2 py-1 text-xs font-semibold text-app-accent hover:bg-app-highlight rounded-lg">
                <Icon type="firefox" size={14} />
                Firefox
              </a>
              <a href={EXTENSION_URLS.edge} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 border border-app-line/80 px-2 py-1 text-xs font-semibold text-app-accent hover:bg-app-highlight rounded-lg">
                <Icon type="edge" size={14} />
                Edge
              </a>
            </div>
          </div>
        </div>
      </div>

      {showTour && (
        <div className="border border-app-line bg-app-surface p-4 rounded-2xl shadow-app-card">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-app-surface text-app-ink">
              <SparklesIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-app-ink font-display">Take the quick tour</h3>
              <p className="mt-1 text-xs leading-5 text-app-muted">
                Learn the dashboard in under a minute.
              </p>
              <button
                type="button"
                onClick={onStartProductTour}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-app-ink hover:text-app-ink"
              >
                Show me around
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
